"use client";

import { ArrowBack, Close } from "@mui/icons-material";
import styles from "./EmbeddedToolbar.module.css";
import { useEmbedded } from "~/hooks/embedded";
import { ReactElement } from "react";
import { Text } from "../Text/Text";

interface Props {
  title?: string;
  subTitle?: string;
  onBack: () => void;
  design?: "original" | "primary" | "white" | "black" | "transparent";
  backIcon?: "back" | "close";
  children?: ReactElement | ReactElement[];
}

export function EmbeddedToolbarButton(props: {
  onClick?: () => void;
  children: ReactElement;
  color: "over-primary" | "white" | "black";
  marginLeft?: 0 | 10;
  marginRight?: 0 | 10;
}) {
  let className: string | undefined;
  switch (props.color) {
    case "over-primary":
      className = styles.buttonOverPrimary;
      break;
    case "white":
      className = styles.buttonWhite;
      break;
    case "black":
      className = styles.buttonBlack;
      break;

    default:
      break;
  }

  return (
    <button
      className={className}
      onClick={props.onClick}
      style={{
        marginRight: props.marginRight !== undefined ? props.marginRight : 10,
        marginLeft: props.marginLeft !== undefined ? props.marginLeft : 0,
      }}
    >
      {props.children}
    </button>
  );
}

export function EmbeddedToolbarButtonOverTransparent(props: {
  onClick?: () => void;
  children: ReactElement;
  marginLeft?: 0 | 10;
  marginRight?: 0 | 10;
}) {
  return (
    <button
      onClick={props.onClick}
      className={styles.buttonOverTransparent}
      style={{
        marginRight: props.marginRight !== undefined ? props.marginRight : 10,
        marginLeft: props.marginLeft !== undefined ? props.marginLeft : 0,
        borderRadius: 100,
        height: 40,
        width: 40,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {props.children}
    </button>
  );
}

export function EmbeddedToolbar(props: Props): React.ReactElement {
  const { isEmbedded } = useEmbedded();

  if (!isEmbedded) return <></>;

  // This ReactNativeWebView?.safeAreaInset needed for Android
  const safeAreaInset =
    typeof window !== "undefined" && window.ReactNativeWebView?.safeAreaInset;

  const containerStyle = safeAreaInset
    ? {
        paddingTop: `${5 + safeAreaInset.top}px`,
      }
    : {};

  const design = props.design || "original";

  if (design === "original") {
    return (
      <div
        className={`${styles.toolbar} embeddedToolbar`} // embeddedToolbar is used by SearchCategoryBar for the sticky top and drop shadow IntersectObserver
        style={containerStyle}
      >
        <button className={styles.button} onClick={() => props.onBack()}>
          <ArrowBack />
        </button>
        <div className={styles.title}>
          <span>{props.title}</span>
        </div>
        <div className={styles.spacer} />
      </div>
    );
  }

  let className: string | undefined;
  let buttonColor: "over-primary" | "black" | "white" = "black";

  switch (props.design) {
    case "primary":
      className = styles.toolbarPrimary;
      buttonColor = "over-primary";
      break;
    case "transparent":
      className = styles.toolbarTransparent;
      buttonColor = "black";
      break;
    case "black":
      className = styles.toolbarBlack;
      buttonColor = "white";
      break;
    case "white":
      className = styles.toolbarWhite;
      buttonColor = "black";
      break;
    default:
      break;
  }

  return (
    <div className={className} style={containerStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        <div style={{ display: "flex" }}>
          {props.design === "transparent" && (
            <EmbeddedToolbarButtonOverTransparent
              onClick={() => props.onBack()}
            >
              {props.backIcon === "close" ? (
                <Close fontSize={"small"} />
              ) : (
                <ArrowBack fontSize={"small"} />
              )}
            </EmbeddedToolbarButtonOverTransparent>
          )}
          {props.design !== "transparent" && (
            <EmbeddedToolbarButton
              onClick={() => props.onBack()}
              color={buttonColor}
            >
              {props.backIcon === "close" ? <Close /> : <ArrowBack />}
            </EmbeddedToolbarButton>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexGrow: 1,
            alignItems: "center",
          }}
        >
          <div style={{ flexGrow: 1 }}>
            <span
              style={
                props.design === "transparent"
                  ? { color: "white", textShadow: "black 1px 0 10px" }
                  : undefined
              }
            >
              {Boolean(props.title && props.subTitle) && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Text size="standard" weight="semi-strong">
                    {props.title}
                  </Text>
                  <Text size="small">{props.subTitle}</Text>
                </div>
              )}
              {Boolean(props.title && !props.subTitle) && (
                <div>
                  <Text size="large" weight="semi-strong">
                    {props.title}
                  </Text>
                </div>
              )}
            </span>
          </div>
          {Boolean(props.children) && (
            <div
              style={{
                display: "flex",
                flexGrow: 0,
              }}
            >
              {props.children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
